import { create } from 'zustand';
import { supabase } from './supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { getApiUrl } from './api';

interface ActivationCode {
  code: string;
  expiresAt: string;
}

interface ActivationStatus {
  status: 'pending' | 'activated' | 'expired';
  sessionToken?: string;
  refreshToken?: string;
  userId?: string;
  expiresAt?: string;
}

interface AuthState {
  user: User | null;
  authenticated: boolean;
  loading: boolean;
  isAdmin: boolean;
  accessToken: string | null;
  
  // Actions
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  checkSession: () => Promise<void>;
  continueAsGuest: () => void;
  setLoading: (loading: boolean) => void;
  getAuthHeaders: () => Record<string, string>;
  
  // Device Activation
  initiateDeviceActivation: () => Promise<ActivationCode>;
  checkActivationStatus: (code: string) => Promise<ActivationStatus>;
  completeDeviceActivation: (code: string, sessionToken: string, refreshToken: string) => Promise<void>;
}

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  isAdmin: boolean;
}

export const useAuthStore = create<AuthState>((set, get) => {
  return {
  user: null,
  authenticated: false,
  loading: false,
  isAdmin: false,
    accessToken: null,

    signIn: async () => {
      try {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) {
          console.error('Google sign-in error:', error);
        }
      } catch (error) {
        console.error('Sign-in error:', error);
      }
  },

  signOut: async () => {
    try {
      set({ loading: true });
      
        const { error } = await supabase.auth.signOut();

        if (error) {
          console.error('Sign out error:', error);
        } else {
        set({
          user: null,
          authenticated: false,
          isAdmin: false,
            accessToken: null,
        });
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      set({ loading: false });
    }
  },

  checkSession: async () => {
    try {
      set({ loading: true });
      
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          set({
            user: null,
            authenticated: false,
            isAdmin: false,
            accessToken: null,
      });
          return;
        }

        if (session?.user) {
          // Get admin status from backend
          const backendSession = await getBackendUserInfo(session.access_token);
          
          set({
            user: {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.user_metadata?.name || session.user.email || 'User',
              picture: session.user.user_metadata?.avatar_url || '',
              isAdmin: backendSession?.isAdmin || false,
            },
            authenticated: true,
            isAdmin: backendSession?.isAdmin || false,
            accessToken: session.access_token,
          });
        } else {
          set({
            user: null,
            authenticated: false,
            isAdmin: false,
            accessToken: null,
          });
        }
      } catch (error) {
        console.error('Session check error:', error);
        set({
          user: null,
          authenticated: false,
          isAdmin: false,
          accessToken: null,
      });
    } finally {
      set({ loading: false });
    }
  },

  continueAsGuest: () => {
    set({
      user: null,
      authenticated: false,
      isAdmin: false,
      loading: false,
        accessToken: null,
    });
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },

    getAuthHeaders: () => {
      const { accessToken } = get();
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return headers;
    },

    // Device Activation Methods
    initiateDeviceActivation: async (): Promise<ActivationCode> => {
      try {
        const response = await fetch(getApiUrl('DEVICE_ACTIVATE_INITIATE'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}), // Send empty JSON object to satisfy Fastify
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to generate activation code');
        }

        const data = await response.json();
        return {
          code: data.code,
          expiresAt: data.expiresAt
        };
      } catch (error: any) {
        console.error('Error initiating device activation:', error);
        throw error;
      }
    },

    checkActivationStatus: async (code: string): Promise<ActivationStatus> => {
      try {
        const response = await fetch(`${getApiUrl('DEVICE_ACTIVATE_STATUS')}/${code}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to check activation status');
        }

        const data = await response.json();
        return {
          status: data.status,
          sessionToken: data.sessionToken,
          refreshToken: data.refreshToken,
          userId: data.userId,
          expiresAt: data.expiresAt
        };
      } catch (error: any) {
        console.error('Error checking activation status:', error);
        throw error;
      }
    },

    completeDeviceActivation: async (code: string, sessionToken: string, refreshToken: string): Promise<void> => {
      try {
        // Set the session using the tokens from activation
        const { data, error } = await supabase.auth.setSession({
          access_token: sessionToken,
          refresh_token: refreshToken
        });

        if (error) {
          throw new Error(`Failed to set session: ${error.message}`);
        }

        if (!data.session) {
          throw new Error('No session returned from activation');
        }

        // Trigger session check to update auth store
        const { checkSession } = get();
        await checkSession();

        console.log('✅ Device activation completed successfully');
      } catch (error: any) {
        console.error('Error completing device activation:', error);
        throw error;
      }
    },
  };
});

// Helper function to get user info from backend (including admin status)
async function getBackendUserInfo(accessToken: string): Promise<{ isAdmin: boolean } | null> {
  try {
    const response = await fetch(getApiUrl('AUTH_USER_INFO'), {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Backend user info:', data);
      return data;
    } else {
      console.error('❌ Backend user info failed:', response.status, await response.text());
    }
  } catch (error) {
    console.error('❌ Failed to get backend user info:', error);
  }
  return null;
}

// Setup auth state change listener
supabase.auth.onAuthStateChange((event, session) => {
  const { checkSession, signOut } = useAuthStore.getState();
  
  if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    checkSession();
  } else if (event === 'SIGNED_OUT') {
    useAuthStore.setState({
      user: null,
      authenticated: false,
      isAdmin: false,
      accessToken: null,
    });
  }
});
